import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import * as Sentry from '@sentry/nextjs';

import { useHasMounted } from '../../../../hooks';
import { api } from '../../../../services';
import { AuthStore } from '../../../../stores';
import { ReactionTypes, ReactionValueTypes } from '../../constants';
import { ReactionContainer } from './Reactions.styles';
import ReactinButton from './ReactionButton';
import ReactionLoader from './ReactionLoader';

type Ids = {
  resourceType?: string | number;
  articleId: string;
  className?: string;
};

type ReactionsProps = Ids;

function getReactions(resourceType: string | number, articleId: string) {
  const url = `/blog/${resourceType}/${articleId}/reactions/`;
  return api.get(url);
}

function Reactions({
  resourceType,
  articleId,
  className,
}: ReactionsProps): JSX.Element {
  const { isLoggedIn } = AuthStore;
  const [isLoadingReactions, setIsLoadingReactions] = useState(true);
  const [reactions, setReactions] = useState();
  const hasMounted = useHasMounted();

  useEffect(() => {
    async function fetchReactions() {
      try {
        const reponse = await getReactions(resourceType, articleId);

        setReactions(reponse?.data);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoadingReactions(false);
      }
    }
    if (resourceType && articleId) {
      fetchReactions();
    }
  }, [resourceType, articleId, isLoggedIn]);

  function addReaction(type: ReactionValueTypes) {
    const url = `/blog/${resourceType}/${articleId}/reactions/`;
    return api.post(url, {
      reaction_type: type,
    });
  }

  function removeReaction(type: ReactionValueTypes) {
    const url = `/blog/${resourceType}/${articleId}/reactions/${type}/`;
    return api.delete(url);
  }

  if (!hasMounted || isLoadingReactions) {
    return <ReactionLoader className={className} />;
  }

  return (
    <ReactionContainer className={className}>
      <ReactinButton
        type={ReactionTypes.HEART}
        reactions={reactions}
        addReaction={addReaction}
        removeReaction={removeReaction}
      />
      <ReactinButton
        type={ReactionTypes.THUMBSUB}
        reactions={reactions}
        addReaction={addReaction}
        removeReaction={removeReaction}
      />
      <ReactinButton
        type={ReactionTypes.SMILE}
        reactions={reactions}
        addReaction={addReaction}
        removeReaction={removeReaction}
      />
    </ReactionContainer>
  );
}

Reactions.defaultProps = {
  resourceType: 1,
  className: '',
};

export default observer(Reactions);
